'use client'

import { useState } from 'react'
import { cn } from '../../utils/cn'
import type { DeployPipelineProps, PipelineStep, PipelineLog } from './types'

function StepIcon({ status }: { status: PipelineStep['status'] }) {
  if (status === 'success') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-primary"><polyline points="20 6 9 17 4 12" /></svg>
  if (status === 'error') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-destructive"><path d="M18 6L6 18M6 6l12 12" /></svg>
  if (status === 'running') return <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  if (status === 'skipped') return <div className="w-4 h-4 rounded-full border-2 border-border border-dashed opacity-40" />
  return <div className="w-4 h-4 rounded-full border-2 border-border" />
}

function LogLine({ log }: { log: PipelineLog }) {
  const color = {
    info: 'text-muted-foreground', warn: 'text-amber-400', error: 'text-destructive', success: 'text-primary',
  }[log.level]
  return (
    <div className="flex gap-2 text-[11px] font-mono">
      <span className="text-muted-foreground/50 shrink-0">{log.timestamp}</span>
      <span className={color}>{log.message}</span>
    </div>
  )
}

export function DeployPipeline({ steps, title, onRetry, className }: DeployPipelineProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const totalDone = steps.filter(s => s.status === 'success' || s.status === 'skipped').length
  const overallProgress = steps.length > 0 ? (totalDone / steps.length) * 100 : 0

  return (
    <div className={cn('rounded-xl border border-border bg-card', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title || 'Pipeline'}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{totalDone} / {steps.length} steps complete</p>
        </div>
        <span className={cn('text-xs font-bold px-2 py-1 rounded-md',
          overallProgress === 100 ? 'bg-primary/15 text-primary' :
          steps.some(s => s.status === 'error') ? 'bg-destructive/15 text-destructive' :
          'bg-muted text-muted-foreground'
        )}>
          {overallProgress === 100 ? 'Complete' : steps.some(s => s.status === 'running') ? 'Running...' : steps.some(s => s.status === 'error') ? 'Failed' : 'Queued'}
        </span>
      </div>

      {/* Overall progress */}
      <div className="h-1 bg-border">
        <div className={cn('h-full transition-all duration-700 ease-out',
          steps.some(s => s.status === 'error') ? 'bg-destructive' : 'bg-primary'
        )} style={{ width: `${overallProgress}%` }} />
      </div>

      {/* Steps */}
      <div className="divide-y divide-border">
        {steps.map(step => {
          const isExpanded = expandedId === step.id
          const hasLogs = step.logs && step.logs.length > 0
          return (
            <div key={step.id}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : step.id)}
                className="flex items-center gap-3 w-full px-5 py-3 text-left hover:bg-muted/20 transition-colors"
              >
                <StepIcon status={step.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn('text-sm font-medium', step.status === 'success' ? 'text-muted-foreground' : 'text-foreground')}>
                      {step.label}
                    </span>
                    {step.duration && <span className="text-[10px] text-muted-foreground/60 font-mono">{step.duration}</span>}
                  </div>
                  {/* Step progress bar */}
                  {step.status === 'running' && step.progress !== undefined && (
                    <div className="h-1 mt-1.5 rounded-full bg-border overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${step.progress}%` }} />
                    </div>
                  )}
                </div>
                {step.status === 'error' && onRetry && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRetry(step.id) }}
                    className="px-2 py-1 text-[10px] font-semibold rounded-md bg-destructive/10 border border-destructive/20 text-destructive hover:bg-destructive/20 transition-colors"
                  >
                    Retry
                  </button>
                )}
                {hasLogs && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className={cn('text-muted-foreground/50 transition-transform', isExpanded && 'rotate-180')}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </button>
              {isExpanded && hasLogs && (
                <div className="px-5 pb-3 pl-12 space-y-0.5 bg-muted/10">
                  {step.logs!.map((log, i) => <LogLine key={i} log={log} />)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
